import CircularProgress from "@mui/material/CircularProgress";
// libraries
import * as Sentry from "@sentry/react";
import { bool } from "aws-sdk/clients/signer";
import { useCallback, useEffect, useState } from "react";

import PrimaryButton from "../../../../components/common/PrimaryButton";
// components
import PaymentModal from "../../../../components/payment/paymentDialog";
import PaymentMethods from "../../../../components/payment/paymentMethods";
import AddressModification from "../../../../components/userProfile/address/address";
import { USER_ROLE } from "../../../../enums";
import {
  ESenderRole,
  IContractDetailsResponse,
} from "../../../../setup/api/services/contract/models";
// apis
import { getCheckoutId } from "../../../../setup/api/services/payment";
import { PaymentBrand } from "../../../../setup/api/services/payment/models";
import { getMyInfo } from "../../../../setup/api/services/profile";
import { IMyInfoData } from "../../../../setup/api/services/profile/models";
import {
  getBasicUserDetailsById,
  getUserAllAddress,
} from "../../../../setup/api/services/user";
// types
import {
  IUserAddress,
  IUserInfoResponse,
} from "../../../../setup/api/services/user/models";
// providers context
import { AuthConfig } from "../../../../setup/context/config/authConfig";
import { ADDRESS, BUTTON } from "./constant";
// styles
import styles from "./styles.module.scss";

interface Props {
  contract: IContractDetailsResponse["data"];
}

const Payment = ({ contract }: Props) => {
  const userInfo = AuthConfig.getMyInfo();
  const [addAddress, setAddAddress] = useState<boolean>(false);
  const [checkoutId, setCheckoutId] = useState("");
  const [userFetchedInfo, setUserInfo] = useState<IMyInfoData>();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState<IUserAddress | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [checkoutDataForAmpli, setCheckoutDataForAmpli] = useState({});
  const [paymentButtonDisabled, setPaymentButtonDisabled] =
    useState<bool>(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    setAddAddress(false);
    setCheckoutId("");
    getAddress();
  }, []);
  const toggleAddAddress = () => {
    setAddAddress(!addAddress);
  };

  //   contract.senderId == userId contract user
  const beneficiaryId =
    contract.senderId == userInfo?.id
      ? contract.receiverId || contract.senderId
      : contract.senderId;
  const [beneficiaryDetails, setBeneficiaryDetials] = useState<
    IUserInfoResponse["data"] | undefined
  >(undefined);

  const [userDetails, setUserDetials] = useState<
    IUserInfoResponse["data"] | undefined
  >(undefined);

  const getAddress = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserAllAddress(Number(userInfo?.id));
      if (response?.data.length > 0) {
        const currentAddress = response.data[0];
        if (currentAddress.id) {
          setAddress(currentAddress);
        } else {
          setAddress(undefined);
        }
        setLoading(false);
      } else {
        setAddress(undefined);
        setLoading(false);
      }
    } catch (error) {
      Sentry.captureException(error);
      setAddress(undefined);
      setLoading(false);
    }
  }, [userInfo?.id]);

  useEffect(() => {
    if (userInfo?.id) getAddress();
  }, [getAddress, userInfo?.id]);

  const getUserInfo = useCallback(async () => {
    try {
      const response = await getMyInfo();
      setUserInfo(response?.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  useEffect(() => {
    if (open) getUserInfo();
  }, [getUserInfo, open]);

  const fetchBeneficiaryDetails = useCallback(async (beneficiaryId: number) => {
    if (beneficiaryId) {
      try {
        const response = await getBasicUserDetailsById(Number(beneficiaryId));
        setBeneficiaryDetials(response.data);
      } catch (error) {
        Sentry.captureException(error);
        console.error("error", error);
      }
    }
  }, []);

  useEffect(() => {
    if (beneficiaryId) fetchBeneficiaryDetails(Number(beneficiaryId));
  }, [fetchBeneficiaryDetails, beneficiaryId]);

  const fetchUserDetails = useCallback(async (userId: number) => {
    if (userId) {
      try {
        const response = await getBasicUserDetailsById(Number(userId));
        setUserDetials(response.data);
      } catch (error) {
        Sentry.captureException(error);
        console.error("error", error);
      }
    }
  }, []);

  useEffect(() => {
    if (userInfo?.id) fetchUserDetails(Number(userInfo?.id));
  }, [fetchUserDetails, userInfo?.id]);

  const getContractType = useCallback(() => {
    if (
      (contract.senderId == userInfo?.id &&
        contract.senderRole === ESenderRole.PROVIDER) ||
      (contract.senderId != userInfo?.id &&
        contract.senderRole === ESenderRole.CUSTOMER)
    ) {
      return USER_ROLE.SELLER;
    } else {
      return USER_ROLE.BUYER;
    }
  }, [contract.senderId, contract.senderRole, userInfo?.id]);

  const handleCheckout = useCallback(
    async (paymentBrand: PaymentBrand): Promise<void> => {
      setPaymentButtonDisabled(true);
      if (
        beneficiaryId &&
        beneficiaryDetails?.profile?.fullName &&
        userInfo?.id &&
        userDetails?.profile?.fullName &&
        userFetchedInfo
      ) {
        const data = {
          amount: contract?.itemPrice + contract?.shipment!.fee,
          beneficiaryId: beneficiaryId,
          beneficiaryName: beneficiaryDetails?.profile?.fullName,
          contractId: contract?.id,
          currency: contract?.currency,
          customerType: getContractType(),
          otherUserId: userFetchedInfo?.id,
          otherUserName: userDetails?.profile?.fullName,
          paymentBrand: paymentBrand,
          paymentType: "DB",
          fees: contract?.fee,
          shortContractId: contract.shortId,
          customer: {
            email: `${userFetchedInfo?.phoneNumber}@gmail.com`,
            givenName: userFetchedInfo?.profile?.firstName,
            surname: userFetchedInfo?.profile?.lastName,
          },
        };

        try {
          const response = await getCheckoutId(data);
          setPaymentButtonDisabled(false);
          setCheckoutId(response?.data?.id);
          setCheckoutDataForAmpli(data);
        } catch (error) {
          Sentry.captureException(error);
          console.error(error);
        }
      }
    },
    [
      beneficiaryDetails?.profile?.fullName,
      beneficiaryId,
      contract?.currency,
      contract?.fee,
      contract?.id,
      contract?.itemPrice,
      contract?.shipment,
      contract.shortId,
      userDetails?.profile?.fullName,
      userFetchedInfo,
      userInfo?.id,
    ]
  );
  return userFetchedInfo ? (
    <div>
      <PaymentModal open={open} handleClose={handleClose}>
        {loading ? (
          <div className={styles["container"]}>
            <CircularProgress size={"small"} />
          </div>
        ) : (
          <>
            {address ? (
              <PaymentMethods
                disabled={paymentButtonDisabled}
                contractId={contract.id}
                checkoutId={checkoutId}
                dataForAmpli={checkoutDataForAmpli}
                handleCheckout={(brand) => handleCheckout(brand)}
              />
            ) : !addAddress ? (
              <div className={styles["container_modal"]}>
                <img alt="img" src="/assets/images/svg/warning.svg" />
                <div className={styles.text_wrapper}>
                  <h1 className={styles.no_bank_header}>
                    {ADDRESS.ENTER_INFO}
                  </h1>
                  <p className={styles.no_bank_desc}>
                    {ADDRESS.ENTER_INFO_DESC}
                  </p>
                </div>
                <PrimaryButton
                  id={`contract_details_toggle_address`}
                  onClick={toggleAddAddress}>
                  {ADDRESS.PROCEED}
                </PrimaryButton>
              </div>
            ) : (
              <div dir="rtl">
                <AddressModification onEnd={handleClose} />
              </div>
            )}
          </>
        )}
      </PaymentModal>
      <PrimaryButton id={`contract_details_buy_now`} onClick={handleOpen}>
        {BUTTON.BUY_NOW}
      </PrimaryButton>
    </div>
  ) : null;
};

export default Payment;
